/**
 * One rule for reading a sealed `nuance.rig/1` channel list, the way the controller loads it.
 *
 * A 16-bit DMX channel is stored twice: the coarse entry (which names its sibling by `fineDmxOffset` /
 * `fineChannelId`) and an `isFineChannel: true` sibling with the same group/subType. A virtual channel has no
 * DMX slot of its own. Neither is a logical channel: the controller builds neither into a DMX target's runtime
 * fixture (`internal/rig/descriptor.go` skips fine siblings; the controller's sealed-rig e2e drops both).
 *
 * Wherever a logical channel's width NUMBER is required (the assignment layout gate's rig side in `scores.ts`, the
 * console's projection in `roomRig.ts`), it is `requiredWidth`: every channel of an OSC target is 16 (an OSC
 * channel has no DMX width; the controller's loader, `internal/rig/sealed.go` `case "osc"`, gives each one
 * `BitWidth: 16` and drives it at that resolution, and Studio exports OSC channels at 16), and any other channel is
 * 16 exactly when it names its fine sibling, else 8.
 *
 * The channels a target contributes to the Remote Live frame vector are `manifestChannels`: every channel of an
 * OSC target, in element order (the controller's loader keeps each one and has no fine-sibling or virtual notion
 * there), and the logical channels of any other target. The manifest carries no width.
 *
 * MIRRORED: `functions/src/lib/sealedChannels.ts` and `web/src/lib/sealedChannels.ts` are byte-identical;
 * `test/sealed-channel-rule-parity.test.ts` pins that they decide alike.
 */

/** The fields of a sealed channel this rule reads. */
export interface SealedChannelFlags {
  isFineChannel?: boolean;
  isVirtual?: boolean;
  fineDmxOffset?: number;
  fineChannelId?: string;
}

/** Whether a sealed channel is a logical channel: neither a fine sibling nor virtual. */
export function isLogicalChannel(channel: SealedChannelFlags): boolean {
  return !channel.isFineChannel && !channel.isVirtual;
}

/** Whether a logical channel names its fine sibling by offset or by id: a 16-bit DMX channel. */
export function hasFineSibling(channel: SealedChannelFlags): boolean {
  return channel.fineDmxOffset !== undefined || Boolean(channel.fineChannelId);
}

/** The fields of a sealed target this rule reads. */
export interface SealedTargetTransport {
  transport?: { protocol?: string };
}

/** A sealed target with the channel lists `manifestChannels` reads. */
export interface SealedTarget<C extends SealedChannelFlags> extends SealedTargetTransport {
  elements: ReadonlyArray<{ channels: readonly C[] }>;
}

/** Whether a target is driven over OSC, the one transport with no DMX channel notions. */
function isOscTarget(target: SealedTargetTransport): boolean {
  return target.transport?.protocol === 'osc';
}

/** A logical channel's required width: 16 on an OSC target, otherwise 16 exactly when it has a fine sibling. */
export function requiredWidth(target: SealedTargetTransport, channel: SealedChannelFlags): 8 | 16 {
  if (isOscTarget(target)) return 16;
  return hasFineSibling(channel) ? 16 : 8;
}

/** A target's Remote Live channels in element order: all of an OSC target's, a DMX target's logical ones. */
export function manifestChannels<C extends SealedChannelFlags>(target: SealedTarget<C>): C[] {
  const channels = target.elements.flatMap((element) => element.channels);
  return isOscTarget(target) ? channels : channels.filter(isLogicalChannel);
}
